/**
 * One export in a list: what it is, whether it is being made, and its download while the file
 * exists. Shared by the accountant's export page and the full export under Settings.
 */

import { Download, LoaderCircle } from "lucide-react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/Field";
import { formatCalendarDate } from "@/lib/dates";
import { useDownloadExport, type Export } from "./queries";

export function ExportRow({ item }: { item: Export }) {
  const { t } = useTranslation();
  const download = useDownloadExport();
  const gone = download.isError;

  return (
    <li className="space-y-2 px-4 py-3">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-stone-800">
            {item.kind === "full" ? t("data.everything") : item.business_name}
          </p>
          <p className="text-sm text-stone-500">
            {item.kind === "full"
              ? t("data.askedOn", { date: formatMoment(item.created_at) })
              : periodText(item, t)}
          </p>
        </div>
        {item.status === "ready" && !gone && (
          <Button
            type="button"
            variant="secondary"
            className="w-auto shrink-0 px-3"
            disabled={download.isPending}
            onClick={() => download.mutate(item.id)}
          >
            <span className="inline-flex items-center gap-1.5">
              <Download className="h-4 w-4" aria-hidden />
              {t("reports.export.download")}
            </span>
          </Button>
        )}
      </div>
      {(item.status === "queued" || item.status === "running") && (
        <p role="status" className="flex items-center gap-2 text-sm text-stone-600">
          <LoaderCircle className="text-brand-700 h-4 w-4 animate-spin" aria-hidden />
          {item.kind === "full" ? t("data.making") : t("reports.export.making")}
        </p>
      )}
      {item.status === "ready" && !gone && item.expires_at && (
        <p className="text-sm text-stone-500">
          {t("reports.export.keptUntil", { date: formatMoment(item.expires_at) })}
        </p>
      )}
      {item.status === "failed" && (
        <p className="text-sm text-red-700">{item.error || t("reports.export.errors.failed")}</p>
      )}
      {(item.status === "expired" || gone) && (
        <p className="text-sm text-stone-500">{t("reports.export.expired")}</p>
      )}
    </li>
  );
}

function periodText(item: Export, t: TFunction): string {
  if (item.start && item.end) {
    return t("reports.export.between", {
      start: formatCalendarDate(item.start),
      end: formatCalendarDate(item.end),
    });
  }
  if (item.start) return t("reports.export.since", { date: formatCalendarDate(item.start) });
  if (item.end) return t("reports.export.until", { date: formatCalendarDate(item.end) });
  return t("reports.periods.all");
}

/** A moment, such as when a file is deleted, as a date in the device's own zone. */
function formatMoment(iso: string, locale = "en-CA"): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(new Date(iso));
}
